import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { ApAuthenticationService, RegistrationStage } from '@ap/browser-authenticator';
import { ApContentService } from '@ap/content';
import { SessiebeheerResource, Session, SessionStatus, UserType } from '@ap/interface-apic-sessiebeheer';
import { ApLoggingService } from '@ap/logging';
import { MijnLaunchDarklyService } from '@ap/mijn-kanaalconfiguratie';
import { ApModalService, ModalClosedReason, ModalOptions, ModalSize } from '@ap/modal';
import { ApPersistenceService } from '@ap/persistence';
import { PendingToken, SignCredentials, SignToken } from '@ap/sign';

import { DigicodeHerstelNotification } from '../../klant/digicode-herstel/digicode-herstel.model';
import { ErrorContext } from '../../klant/login/error/error.model';
import { MijnLoginAnalyticsService } from '../analytics/mijn-login-analytics.service';
import { LoginEnabledService } from '../login-enabled-guard/login-enabled.service';
import { Notification, NotificationText } from '../mijn-login.model';
import { MijnLoginNavigation } from '../mijn-login.navigation';
import { MijnLoginService } from '../mijn-login.service';

import { BrowserOutdatedModalComponent } from './browser-outdated-modal/browser-outdated-modal.component';
import {
    ANALYTICS_CLICKACTION_TOBROWSERPAGE,
    ANALYTICS_CLICKACTION_TOLOGIN,
    ANALYTICS_CLICKED_ELEMENT,
    AriaLabels,
    CONVERSATIONAL_SESSION,
    HuismaprequestSession,
    InstantNotificationContentKey,
    MIJN_OMGEVING_SESSION,
    NBA_SESSION,
    OVERBOEKEN_SESSION,
    RegularRequestSession
} from './login.wrapper.constants';
import content from './login.wrapper.content.json';
import { LoginWrapperService } from './login.wrapper.service';

/**
 * @ngdoc component
 * @name LoginWrapperComponent
 * @module LoginWrapperModule
 */
@Component({
    selector: 'inloggen',
    templateUrl: './login.wrapper.component.html',
    styleUrls: ['./login.wrapper.component.scss'],
    standalone: false
})
export class LoginWrapperComponent implements OnInit {
    public actionId: string;
    public orgurl: string;
    public orgurlFragment: string;
    public additionalTokens: SignToken[] = [];
    public showCiam: boolean;

    public instantNotification: Notification;
    public genericNotification: Notification;

    public showSessionNotification = false;
    public showRedirect = false;
    public preferredToken: SignToken;
    public tokenType: SignToken;

    public digicodeHerstelNotification: DigicodeHerstelNotification;
    public ariaLabels = AriaLabels;

    public isHuismapLogin: boolean;

    public isCiamEnabled: Signal<boolean>;

    public constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly contentService: ApContentService,
        private readonly logger: ApLoggingService,
        private readonly loginPageService: LoginWrapperService,
        private readonly mijnLoginAnalyticsService: MijnLoginAnalyticsService,
        private readonly mijnLoginNavigation: MijnLoginNavigation,
        private readonly mijnLoginService: MijnLoginService,
        private readonly persistenceService: ApPersistenceService,
        private readonly sessiebeheerResource: SessiebeheerResource,
        private readonly apAuthenticationService: ApAuthenticationService,
        private readonly modalService: ApModalService,
        private readonly loginEnabledService: LoginEnabledService,
        private readonly launchDarklyService: MijnLaunchDarklyService
    ) {
        this.isCiamEnabled = toSignal(this.launchDarklyService.getFlagValue<boolean>('ciam-enabled', false, true), { initialValue: false });
    }

    private static overridePreferredToken(tokenParam: string): SignToken {
        if (tokenParam != null && SignToken[tokenParam] === tokenParam) {
            return SignToken[tokenParam];
        }
        return null;
    }

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
        this.digicodeHerstelNotification = this.mijnLoginService.getNotificationMessage();
        this.mijnLoginAnalyticsService.pushPageView();

        this.activatedRoute.queryParams.pipe(take(1)).subscribe((params) => {
            this.handleQueryParams(params);
        });

        try {
            window.sessionStorage.setItem('__storage-check__', '');
            window.sessionStorage.removeItem('__storage-check__');
        } catch {
            this.mijnLoginNavigation.toError(ErrorContext.NO_COOKIES);
            return;
        }

        this.canStartLoginSession()
            .pipe(
                switchMap((canStartSession) => {
                    if (canStartSession) {
                        const actionId = this.getActionId();
                        this.clearVolatileSessionVariables();
                        return actionId;
                    }
                    return of('alreadyLoggedIn');
                }),
                tap((actionId) => {
                    if (actionId === 'alreadyLoggedIn') {
                        return of(null);
                    }
                    this.actionId = actionId as string;
                    if (!this.actionId) {
                        this.actionIdError();
                    }
                }),
                switchMap(() => this.getNotificationText()),
                catchError(() => {
                    this.logger.error('Error getting login status');
                    this.mijnLoginNavigation.toError();
                    return of(null);
                })
            )
            .subscribe((contentValue) => {
                if (
                    contentValue != null &&
                    contentValue[InstantNotificationContentKey] !== '' &&
                    contentValue[InstantNotificationContentKey] !== `{?${InstantNotificationContentKey}?}` &&
                    contentValue[InstantNotificationContentKey] !== null
                ) {
                    this.instantNotification = {
                        status: 'info',
                        text: contentValue[InstantNotificationContentKey]
                    };
                }
            });

        if (!this.loginPageService.browserIsUpToDate()) {
            this.openOutdatedBrowserModal();
        }

        this.loginEnabledService.isEnabled().subscribe((isEnabled) => {
            if (!isEnabled) {
                this.mijnLoginNavigation.toError();
            }
        });

        this.activatedRoute.fragment.subscribe((fragment) => (this.orgurlFragment = fragment));
    }

    public setActionId(): void {
        this.getActionId().subscribe((actionId) => {
            if (!actionId) {
                this.actionIdError();
            } else {
                this.actionId = actionId;
            }
        });
    }

    public complete(): void {
        this.loginPageService
            .postSession(this.actionId, this.isHuismapLogin, true)
            .pipe(mergeMap(() => this.sessiebeheerResource.getSession('0')))
            .subscribe({
                next: (result) => {
                    const session = result;

                    if (this.isHuismapLogin) {
                        this.persistenceService.setSession('mijn-login', 'context', 'huismap');
                        this.mijnLoginNavigation.external(this.contentService.get('mijn.login.login.wrapper.redirect.huismap.link'));
                    } else if (this.matchDigicodeSessionId(session)) {
                        this.mijnLoginNavigation.external(
                            this.contentService.get('mijn.login.login.wrapper.redirect.requestOnlineBanking.link')
                        );
                    } else {
                        this.mijnLoginNavigation.external(
                            this.loginPageService.calculateRedirectUrl(this.orgurl, this.orgurlFragment),
                            true
                        );
                        this.mijnLoginAnalyticsService.sendAnalyticsId();
                    }
                },
                error: (errorResponse: HttpErrorResponse) => {
                    this.logger.error('Validating login went wrong', errorResponse);
                    this.mijnLoginNavigation.toError();
                }
            });
    }

    public onPendingToken(pendingToken: PendingToken): void {
        this.loginPageService
            .postSession(this.actionId, this.isHuismapLogin)
            .pipe(
                mergeMap(() => this.apAuthenticationService.getToken(pendingToken.keychainId)),
                withLatestFrom(this.sessiebeheerResource.getSession('0'))
            )
            .subscribe({
                next: ([token, session]) => {
                    if (token.stage === RegistrationStage.CREATED || token.stage === RegistrationStage.VERIFY_TOKEN) {
                        if (session.user && session.user.type.includes(UserType.CUSTOMER)) {
                            this.mijnLoginNavigation.toError(ErrorContext.BROWSERTOKEN);
                        } else {
                            this.loginPageService.setBrowserTokensSession(pendingToken.keychainId);
                            this.mijnLoginNavigation.external(this.contentService.get('mijn.login.login.wrapper.redirect.pega.link'));
                        }
                    } else if (token.stage === RegistrationStage.VERIFY_PERSON) {
                        this.loginPageService.setBrowserTokensSession(pendingToken.keychainId);
                        this.mijnLoginNavigation.external(this.contentService.get('mijn.login.login.wrapper.redirect.pega.link'));
                    }
                },
                error: (errorResponse: HttpErrorResponse) => {
                    this.logger.error('Validating pending token went wrong', errorResponse);
                    this.mijnLoginNavigation.toError();
                }
            });
    }

    public onTokenChanged(signToken: SignToken): void {
        this.tokenType = signToken;

        switch (signToken) {
            case SignToken.DIGICODE:
                this.mijnLoginAnalyticsService.pushSignFormSelection('Digicode inloggen');
                break;
            case SignToken.DIGIPAS:
                this.mijnLoginAnalyticsService.pushSignFormSelection('Digipas inloggen');
                break;
            case SignToken.NATIVE_APP:
                this.mijnLoginAnalyticsService.pushSignFormSelection('Mobiel inloggen');
                break;
            case SignToken.BROWSER:
                this.mijnLoginAnalyticsService.pushSignFormSelection('Browser inloggen');
                break;
        }

        // Trigger analytics when clicking on 'Log Anders In'
        if (!signToken && this.persistenceService.getLocal('ap-sign', 'token') !== undefined) {
            this.mijnLoginAnalyticsService.pushSignFormClick();
        }
    }

    public onLostBrowserCredentials(keychainId: string): void {
        this.mijnLoginService.setKeyChainId(keychainId);
        this.mijnLoginNavigation.toForgotBrowsercode();
    }

    public updatePassword(signCredentials: SignCredentials): void {
        this.mijnLoginService.setChangeUsername(false);
        this.mijnLoginService.setSignCredentials(signCredentials);
        this.mijnLoginNavigation.toChangeCredentials();
    }

    public updateUsernamePassword(signCredentials: SignCredentials): void {
        this.mijnLoginService.setChangeUsername(true);
        this.mijnLoginService.setSignCredentials(signCredentials);
        this.mijnLoginNavigation.toChangeCredentials();
    }

    public getTitle(): string {
        if (this.showRedirect) {
            return this.contentService.get('mijn.login.login.wrapper.redirect.title');
        }
        if (this.isHuismapLogin) {
            return this.contentService.get('mijn.login.login.wrapper.title.huismap');
        }
        return this.contentService.get('mijn.login.login.wrapper.title');
    }

    public getAriaLabelsForNotifications(): string[] {
        return Object.values(AriaLabels);
    }

    public hasAdditionalTokens(): boolean {
        return this.additionalTokens?.length > 0;
    }

    public showExtraContentCard(): boolean {
        return !this.isHuismapLogin && (!this.tokenType || this.tokenType === SignToken.BROWSER);
    }

    public tokenIsBrowserCode(): boolean {
        return this.tokenType === SignToken.BROWSER;
    }

    public sendAnalyticsAndFollowLink(): void {
        this.mijnLoginAnalyticsService.pushClickEvent('hulp-bij-inloggen', 'link');
        this.mijnLoginNavigation.external(this.contentService.get(`mijn.login.login.wrapper.login.problem.url.${this.tokenType}`));
    }

    public loginAvailable(): boolean {
        return this.contentService.get('mijn.login.login.wrapper.auth0-login').length > 0;
    }

    private handleQueryParams(params: Params): void {
        this.isHuismapLogin = params.huismap != null;

        this.orgurl = params.orgurl;
        this.showCiam = params.ciam != null;
        const internal = params.internal === '';
        if (this.orgurl != null && !internal) {
            this.showRedirect = true;
        }
        this.showSessionNotification = params.showConfirmLogoutMessage === 'true';
        if (params.token != null) {
            this.preferredToken = LoginWrapperComponent.overridePreferredToken(params.token.toUpperCase());
        }
        if (this.isHuismapLogin) {
            this.additionalTokens = [SignToken.PROSPECT_DIGICODE];
            this.preferredToken = SignToken.PROSPECT_DIGICODE;
        }
        this.genericNotification = this.getGenericNotification();
    }

    private actionIdError(): void {
        this.logger.error('Error getting actionId');
        this.mijnLoginNavigation.toError();
    }

    private canStartLoginSession(): Observable<boolean> {
        return this.sessiebeheerResource.getSession('0').pipe(
            switchMap((session) => {
                if (session.user) {
                    if (session.user.type.includes(UserType.ANONYMOUS)) {
                        return this.sessiebeheerResource.patchSession('0', { status: SessionStatus.TERMINATED });
                    } else if (session?.authentication_level === HuismaprequestSession.authentication_level) {
                        this.mijnLoginNavigation.external(this.contentService.get('mijn.login.login.wrapper.redirect.huismap.link'));
                        return of(false);
                    }
                    const redirectUrl = this.loginPageService.calculateRedirectUrl(this.orgurl, this.orgurlFragment);
                    if (this.orgurl) {
                        this.mijnLoginNavigation.external(redirectUrl, true);
                    } else {
                        this.mijnLoginNavigation.external(redirectUrl);
                    }
                    return of(false);
                }
                return of(true);
            }),
            map((result) => result !== false)
        );
    }

    private getActionId(): Observable<string | void> {
        const session = this.isHuismapLogin ? HuismaprequestSession : RegularRequestSession;
        session.started_from_device_fingerprint = this.apAuthenticationService.getFingerprint();
        return this.sessiebeheerResource
            .postSession(session)
            .pipe(catchError((error: HttpErrorResponse) => of(error.headers ? error.headers.get('X-Authentication-Task-Id') : null)));
    }

    private getNotificationText(): Observable<NotificationText> {
        if (this.isHuismapLogin) {
            return of(null);
        }
        return this.loginPageService.notificationText();
    }

    private getGenericNotification(): Notification {
        if (this.isHuismapLogin) {
            return {
                status: 'info',
                text: this.contentService.get('mijn.login.login.wrapper.info.notification.huismap.text')
            };
        }
        if (this.contentService.get('mijn.login.login.wrapper.info.notification.text') !== '') {
            return {
                status: 'info',
                text: this.contentService.get('mijn.login.login.wrapper.info.notification.text')
            };
        }
    }

    private clearVolatileSessionVariables(): void {
        this.persistenceService.clearSession(MIJN_OMGEVING_SESSION);
        this.persistenceService.clearSession(CONVERSATIONAL_SESSION);
        this.persistenceService.clearSession(NBA_SESSION);
        this.persistenceService.clearSession(OVERBOEKEN_SESSION);
    }

    private openOutdatedBrowserModal(): void {
        const modalOptions: ModalOptions<void> = new ModalOptions();
        modalOptions.title = this.contentService.get('mijn.login.login.wrapper.outdatedBrowser.title');
        modalOptions.showHeader = true;
        modalOptions.cancelable = false;
        modalOptions.fullscreen = false;
        modalOptions.size = ModalSize.LARGE;

        const modalComponent = this.modalService.open(BrowserOutdatedModalComponent, modalOptions);
        modalComponent.afterClosed().subscribe((result) => {
            this.mijnLoginAnalyticsService.pushClickEvent(
                ANALYTICS_CLICKED_ELEMENT,
                result.reason === ModalClosedReason.ACCEPTED ? ANALYTICS_CLICKACTION_TOBROWSERPAGE : ANALYTICS_CLICKACTION_TOLOGIN
            );
            if (result.reason === ModalClosedReason.ACCEPTED) {
                this.mijnLoginNavigation.external(this.contentService.get('mijn.login.login.wrapper.redirect.changeBrowser.link'));
            }
        });
    }

    private matchDigicodeSessionId(session: Session): boolean {
        return session.authenticated_with_tokens?.some((item) => {
            const tokenType = item.token_type.toLocaleLowerCase();

            return item.id === session.natural_person?.id && (tokenType === 'digicode' || tokenType === 'prospect_digicode');
        });
    }
}
